import { useEffect, useMemo, useState } from "react";
import { Modal, Table, Button, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { EyeFill, EyeSlashFill, Trash} from "react-bootstrap-icons";
import http from "services/httpClient";

interface HeartbeatRecipient {
  id: number;
  device_id: number;
  user_id: number;
  subscribed: boolean;
  alarm: boolean;
  alert_type: "email" | "sms" | "both" | null;
  last_triggered_at?: string | null;
}

interface ClientUser {
  id: number;
  user_id?: number;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  user?: {
    id?: number;
    email?: string;
    first_name?: string | null;
    last_name?: string | null;
    role?: string | null;
  };
}

interface HeartbeatRecipientsModalProps {
  show: boolean;
  onClose: () => void;
  deviceId: number;
  clientId: number;
  smsEnabled: boolean;
  onRecipientsChanged?: () => void;
}

const normalizeUsers = (payload: any): ClientUser[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeHeartbeatRecipients = (payload: any): HeartbeatRecipient[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getUserId = (u: ClientUser): number => {
  return u.user?.id ?? u.user_id ?? u.id;
};

const getUserEmail = (u: ClientUser): string => {
  return u.user?.email ?? u.email ?? "";
};

const getUserFirstName = (u: ClientUser): string => {
  return u.user?.first_name ?? u.first_name ?? "";
};

const getUserLastName = (u: ClientUser): string => {
  return u.user?.last_name ?? u.last_name ?? "";
};

const formatUserLabel = (u: ClientUser) => {
  const fullName = `${getUserFirstName(u)} ${getUserLastName(u)}`.trim();
  const email = getUserEmail(u);

  if (fullName && email) return `${fullName} (${email})`;
  if (email) return email;
  return `User #${getUserId(u)}`;
};

const sortUsers = (users: ClientUser[]): ClientUser[] => {
  return [...users].sort((a, b) => {
    const aName = formatUserLabel(a).toLowerCase();
    const bName = formatUserLabel(b).toLowerCase();
    return aName.localeCompare(bName);
  });
};

const sortHeartbeatRecipients = (
  rows: HeartbeatRecipient[],
  users: ClientUser[]
): HeartbeatRecipient[] => {
  const getRecipientLabel = (rec: HeartbeatRecipient) => {
    const user = users.find((u) => getUserId(u) === rec.user_id);
    return user ? formatUserLabel(user).toLowerCase() : `user #${rec.user_id}`;
  };

  return [...rows].sort((a, b) => {
    return getRecipientLabel(a).localeCompare(getRecipientLabel(b));
  });
};

export const HeartbeatRecipientsModal: React.FC<HeartbeatRecipientsModalProps> = ({
  show,
  onClose,
  deviceId,
  clientId,
  smsEnabled,
  onRecipientsChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState<HeartbeatRecipient[]>([]);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const recipientUserIds = useMemo(() => {
    return new Set(recipients.map((r) => r.user_id));
  }, [recipients]);

  const availableUsers = useMemo(() => {
    return clientUsers.filter((u) => !recipientUserIds.has(getUserId(u)));
  }, [clientUsers, recipientUserIds]);

  const fetchData = async () => {
    if (!show || !deviceId) return;

    try {
        setLoading(true);

        const [recipientsRes, usersRes] = await Promise.all([
            http.get(`/heartbeat_trigger/device/${deviceId}`),
            http.get(`/clients/${clientId}/users`),
        ]);

        const normalizedUsers = sortUsers(normalizeUsers(usersRes.data));
        const normalizedRecipients = sortHeartbeatRecipients(
        normalizeHeartbeatRecipients(recipientsRes.data),
        normalizedUsers
        );

        setClientUsers(normalizedUsers);
        setRecipients(normalizedRecipients);
    } catch (err) {
      console.error("HeartbeatRecipientsModal fetchData error:", err);
      setRecipients([]);
      setClientUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, deviceId, clientId]);

  const refreshRecipientsOnly = async () => {
    const recipientsRes = await http.get(`/heartbeat_trigger/device/${deviceId}`);
    const normalizedRecipients = sortHeartbeatRecipients(
    normalizeHeartbeatRecipients(recipientsRes.data),
    clientUsers
    );
    setRecipients(normalizedRecipients);
    onRecipientsChanged?.();
  };

  const addRecipient = async () => {
    if (!deviceId || !selectedUserId) return;

    try {
      await http.post(`/heartbeat_trigger/device/${deviceId}/recipient/${selectedUserId}`);
      setSelectedUserId("");
      await fetchData();
      onRecipientsChanged?.();
    } catch (err) {
      console.error("HeartbeatRecipientsModal addRecipient error:", err);
    }
  };

  const addAllRecipients = async () => {
    if (!deviceId || availableUsers.length === 0) return;

    try {
      await http.post(
        `/device-heartbeat/device/${deviceId}/add_all`,
        {
          device_id: deviceId,
          user_ids: availableUsers.map((u) => getUserId(u)),
          alert_type: "both",
          subscribed: true,
        }
      );

      await fetchData();
      onRecipientsChanged?.();
    } catch (err) {
      console.error("HeartbeatRecipientsModal addAllRecipients error:", err);
    }
  };

  
  const updateRecipient = async (
    triggerId: number,
    patch: Partial<Pick<HeartbeatRecipient, "alert_type" | "subscribed" | "alarm">>
  ) => {
    try {
      await http.patch(`/heartbeat_trigger/${triggerId}`, patch);
      await refreshRecipientsOnly();
    } catch (err) {
      console.error("HeartbeatRecipientsModal updateRecipient error:", err);
    }
  };

  const removeRecipient = async (triggerId: number) => {
    try {
      await http.delete(`/heartbeat_trigger/${triggerId}`);
      await fetchData();
      onRecipientsChanged?.();
    } catch (err) {
      console.error("HeartbeatRecipientsModal removeRecipient error:", err);
    }
  };

  const resolveUser = (userId: number) => {
    return clientUsers.find((u) => getUserId(u) === userId);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Heartbeat Recipients — Device Offline (24H)</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div className="d-flex gap-2 mb-3">
              <Form.Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Select user...</option>
                {availableUsers.map((u) => (
                  <option key={getUserId(u)} value={getUserId(u)}>
                    {formatUserLabel(u)}
                  </option>
                ))}
              </Form.Select>

              <Button onClick={addRecipient} disabled={!selectedUserId}>
                Add Selected Recipient
              </Button>

              <Button
                onClick={addAllRecipients}
                disabled={availableUsers.length === 0}
              >
                Add All
              </Button>
            </div>

            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th className={!smsEnabled ? "text-muted" : ""}>SMS</th>
                  <th>Subscribed</th>
                  <th>Remove</th>
                </tr>
              </thead>

              <tbody>
                {recipients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      No heartbeat recipients assigned.
                    </td>
                  </tr>
                ) : (
                  recipients.map((rec) => {
                    const user = resolveUser(rec.user_id);
                    const isEmail = rec.alert_type === "email" || rec.alert_type === "both";
                    const isSMS = rec.alert_type === "sms" || rec.alert_type === "both";

                    return (
                      <tr key={rec.id}>
                        <td>{user ? formatUserLabel(user) : `User #${rec.user_id}`}</td>

                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={isEmail}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              const current = rec.alert_type;

                              let newType: HeartbeatRecipient["alert_type"];
                              if (checked) {
                                newType =
                                  current === "sms" || current === "both" ? "both" : "email";
                              } else {
                                newType = current === "both" ? "sms" : null;
                              }

                              await updateRecipient(rec.id, { alert_type: newType });
                            }}
                          />
                        </td>

                        <td className={!smsEnabled ? "text-muted" : ""}>
                          <Form.Check
                            type="checkbox"
                            disabled={!smsEnabled}
                            checked={isSMS}
                            onChange={async (e) => {
                              if (!smsEnabled) return;

                              const checked = e.target.checked;
                              const current = rec.alert_type;

                              let newType: HeartbeatRecipient["alert_type"];
                              if (checked) {
                                newType =
                                  current === "email" || current === "both" ? "both" : "sms";
                              } else {
                                newType = current === "both" ? "email" : null;
                              }

                              await updateRecipient(rec.id, { alert_type: newType });
                            }}
                          />
                        </td>

                        <td className="text-center">
                          <span
                            onClick={() => updateRecipient(rec.id, { subscribed: !rec.subscribed })}
                            style={{ cursor: "pointer" }}
                          >
                            {rec.subscribed ? <EyeFill /> : <EyeSlashFill className="text-muted" />}
                          </span>
                        </td>

                        <td className="text-center">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Remove recipient</Tooltip>}>
                            <Button
                              variant="link"
                              className="p-0 text-danger"
                              onClick={() => removeRecipient(rec.id)}
                            >
                              <Trash />
                            </Button>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HeartbeatRecipientsModal;